import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import Layout from '../../../components/Layout/Layout';
import StatusBadge from '../../../components/StatusBadge/StatusBadge';
import api from '../../../api/axios';
import { FILE_BASE } from '../../../api/config';
import { downloadFile } from '../../../utils/download';
import './TrainingView.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

function PdfViewer({ url }) {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1.2);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('schulung_token');
    pdfjsLib.getDocument({ url, httpHeaders: { Authorization: `Bearer ${token}` } })
      .promise.then(doc => {
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setCurrentPage(1);
        setLoading(false);
      });
  }, [url]);

  const renderPage = useCallback(async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    const canvas = canvasRef.current;
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    const ctx = canvas.getContext('2d');
    const task = page.render({ canvasContext: ctx, viewport });
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (e) {
      if (e?.name !== 'RenderingCancelledException') throw e;
    }
  }, [pdfDoc, scale]);

  useEffect(() => {
    renderPage(currentPage);
  }, [currentPage, renderPage]);

  const prev = () => setCurrentPage(p => Math.max(1, p - 1));
  const next = () => setCurrentPage(p => Math.min(totalPages, p + 1));

  if (loading) return <div className="pdf-loading">PDF wird geladen…</div>;

  return (
    <div className="pdf-gallery">
      <div className="pdf-canvas-wrap">
        <canvas ref={canvasRef} className="pdf-canvas" />
      </div>
      <div className="pdf-nav">
        <button className="pdf-nav-btn" onClick={prev} disabled={currentPage <= 1}>‹</button>
        <span className="pdf-page-info">Seite {currentPage} von {totalPages}</span>
        <button className="pdf-nav-btn" onClick={next} disabled={currentPage >= totalPages}>›</button>
        <div className="pdf-zoom">
          <button className="pdf-zoom-btn" onClick={() => setScale(s => Math.max(0.5, s - 0.2))}>−</button>
          <span>{Math.round(scale * 100)}%</span>
          <button className="pdf-zoom-btn" onClick={() => setScale(s => Math.min(3, s + 0.2))}>+</button>
        </div>
      </div>
    </div>
  );
}

export default function TrainingView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/user-trainings/my'),
      api.get(`/quiz/user-trainings/${id}/attempts`).catch(() => ({ data: [] })),
    ]).then(([trainings, attemptsRes]) => {
      const t = trainings.data.find(t => t.id === Number(id));
      setTraining(t);
      setAttempts(attemptsRes.data);
      // AM-07 / AGB § 5 (6) e: Der erste Aufruf einer Lerneinheit gilt als Bearbeitungsbeginn
      if (t && !t.started_at) {
        api.patch(`/user-trainings/${id}/start`)
          .then(res => setTraining(prev => prev ? { ...prev, started_at: res.data?.startedAt || new Date().toISOString(), status: prev.status === 'pending' ? 'in_progress' : prev.status } : prev))
          .catch(() => {});
      }
    }).finally(() => setLoading(false));
  }, [id]);

  async function downloadCertificate() {
    try {
      await downloadFile(`/user-trainings/${id}/certificate`, `Zertifikat-${training.title.replace(/[^a-z0-9]/gi, '_')}.pdf`);
    } catch {
      alert('Zertifikat konnte nicht heruntergeladen werden');
    }
  }

  if (loading) return <Layout title="Schulung"><div>Laden...</div></Layout>;
  if (!training) return <Layout title="Schulung"><div className="alert alert-error">Schulung nicht gefunden.</div></Layout>;

  const pdfUrl = training.pdf_path ? `${FILE_BASE}/uploads/pdfs/${training.pdf_path}` : null;
  const lastAttempt = attempts[0];

  return (
    <Layout title={training.title}>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/my-trainings" className="btn btn-ghost btn-sm">← Zurück zu meinen Schulungen</Link>
      </div>

      <div className="tv-header card" style={{ marginBottom: '1.5rem' }}>
        <div className="tv-header-left">
          <StatusBadge status={training.status} />
          {training.deadline && (
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-dark)' }}>
              Frist: {new Date(training.deadline).toLocaleDateString('de-DE')}
            </span>
          )}
          {lastAttempt && (
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-dark)' }}>
              Letzter Versuch: {parseFloat(lastAttempt.score).toFixed(0)}%, {lastAttempt.passed ? 'bestanden' : 'nicht bestanden'}
            </span>
          )}
        </div>
        <div className="tv-header-actions">
          <button className="btn btn-primary btn-sm" onClick={() => navigate(`/my-trainings/${id}/quiz`)}>
            {training.status === 'passed' ? 'Quiz wiederholen' : 'Quiz starten'}
          </button>
          {training.status === 'passed' && (
            <button className="btn btn-secondary btn-sm" onClick={downloadCertificate}>
              Zertifikat herunterladen
            </button>
          )}
        </div>
      </div>

      {pdfUrl ? (
        <div className="card tv-pdf-section">
          <div className="tv-pdf-header">
            <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Schulungsunterlagen</h2>
            <a href={pdfUrl} download={training.pdf_original_name} className="btn btn-ghost btn-sm">
              PDF herunterladen
            </a>
          </div>
          <PdfViewer url={pdfUrl} />
          {training.description && (
            <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--sidebar-border)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-dark)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                {training.description}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-dark)' }}>
          Kein PDF verfügbar.
        </div>
      )}

      {attempts.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' }}>Versuchshistorie</h2>
          <div className="table-container">
            <table>
              <thead><tr><th>#</th><th>Note</th><th>Ergebnis</th><th>Datum</th></tr></thead>
              <tbody>
                {attempts.map((a, i) => (
                  <tr key={a.id}>
                    <td>{attempts.length - i}</td>
                    <td style={{ fontWeight: 600 }}>{parseFloat(a.score).toFixed(0)}%</td>
                    <td><span className={`badge ${a.passed ? 'badge-passed' : 'badge-failed'}`}>{a.passed ? 'Bestanden' : 'Nicht bestanden'}</span></td>
                    <td>{new Date(a.attempted_at).toLocaleString('de-DE')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
